import { ColumnMapping, ImportField } from "./types";

export type MappingConfidence = "high" | "low";

export type ColumnSuggestion = {
    columnIndex: number;
    field: string | null;
    confidence: MappingConfidence;
};

/** Detector por contenido: recibe los valores no vacios de una columna y propone un atributo. */
export type ContentDetector = (sample: string[]) => string | null;

export type MappingSuggesterConfig = {
    fields: ImportField[];
    /**
     * Sinonimos por atributo. Es una ayuda para adivinar mejor, NO una lista de encabezados
     * requeridos: los archivos vienen de sistemas externos y las columnas pueden llamarse
     * como sea. Ampliar esta tabla con cada archivo real que aparezca es barato y seguro.
     */
    synonyms?: Record<string, string[]>;
    /** Deduccion por contenido, la unica senal cuando el archivo no trae encabezados. */
    detectByContent?: ContentDetector;
};

/** Puntaje minimo para aceptar una sugerencia por nombre. */
const NAME_SCORE_THRESHOLD = 0.62;

/** Ventaja minima sobre la segunda candidata para no estar adivinando. */
const NAME_SCORE_MARGIN = 0.08;

/** Valores no vacios que se muestrean para deducir el tipo de una columna. */
export const CONTENT_SAMPLE_SIZE = 30;

/** Proporcion de la muestra que debe cumplir un patron para darlo por bueno. */
export const CONTENT_MATCH_RATIO = 0.8;

/** Quita tildes, puntuacion y mayusculas para poder comparar nombres de columna. */
export const normalizeHeader = (raw?: string | null): string =>
    `${raw ?? ""}`
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();

const tokens = (value: string): string[] => value.split(" ").filter(Boolean);

/** Distancia de Levenshtein normalizada a un puntaje de similitud entre 0 y 1. */
const editSimilarity = (a: string, b: string): number => {
    if (!a || !b) return 0;
    if (a === b) return 1;

    const rows = a.length + 1;
    const cols = b.length + 1;
    let previous = Array.from({ length: cols }, (_, index) => index);

    for (let i = 1; i < rows; i++) {
        const current = [i];
        for (let j = 1; j < cols; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            current[j] = Math.min(previous[j] + 1, current[j - 1] + 1, previous[j - 1] + cost);
        }
        previous = current;
    }

    return 1 - previous[cols - 1] / Math.max(a.length, b.length);
};

const tokenOverlap = (a: string, b: string): number => {
    const left = new Set(tokens(a));
    const right = new Set(tokens(b));
    if (!left.size || !right.size) return 0;

    let shared = 0;
    left.forEach((token) => {
        if (right.has(token)) shared += 1;
    });

    return shared / Math.max(left.size, right.size);
};

/**
 * Cuanto del texto mas largo tiene que cubrir el mas corto para que una coincidencia por
 * subcadena valga como sinonimo.
 *
 * Sin este piso, "Descuento Maximo" calzaba con el atributo "maximo" (el maximo de
 * inventario) solo porque lo contiene, y el importador proponia guardar el porcentaje de
 * descuento como stock maximo. Los encabezados abreviados que si valen ("Ced. Juridica")
 * se cubren con sinonimos, que llegan por similitud de edicion.
 */
const SUBSTRING_MIN_COVERAGE = 0.6;

/** Que tan parecido es el nombre de la columna a un candidato (etiqueta, atributo o sinonimo). */
const similarity = (header: string, candidate: string): number => {
    if (!header || !candidate) return 0;
    if (header === candidate) return 1;

    if (header.includes(candidate) || candidate.includes(header)) {
        const coverage = Math.min(header.length, candidate.length) / Math.max(header.length, candidate.length);
        if (coverage >= SUBSTRING_MIN_COVERAGE) return 0.9;
    }

    return Math.max(tokenOverlap(header, candidate), editSimilarity(header, candidate));
};

/** Proporcion de la muestra que cumple el patron. Util para escribir detectores. */
export const sampleRatio = (values: string[], predicate: (value: string) => boolean): number =>
    values.length ? values.filter(predicate).length / values.length : 0;

/** `true` si al menos `CONTENT_MATCH_RATIO` de la muestra cumple el patron. */
export const sampleMatches = (values: string[], predicate: (value: string) => boolean): boolean =>
    sampleRatio(values, predicate) >= CONTENT_MATCH_RATIO;

export type MappingSuggester = {
    suggestFieldByName: (header: string) => string | null;
    suggestFieldByContent: (values: string[]) => string | null;
    suggestColumnSuggestions: (columnNames: string[], dataRows: string[][]) => ColumnSuggestion[];
    suggestColumnMapping: (columnNames: string[], dataRows: string[][]) => ColumnMapping;
};

/**
 * Arma el auto-mapeo de un modelo combinando el nombre de la columna y su contenido.
 *
 * Todo lo que devuelve es una sugerencia editable: dejar columnas sin mapear es un
 * resultado valido y esperado, porque los archivos traen columnas que el sistema no usa.
 */
export const createMappingSuggester = (config: MappingSuggesterConfig): MappingSuggester => {
    const { fields, synonyms = {}, detectByContent } = config;

    const candidatesFor = (field: ImportField): string[] => [
        normalizeHeader(field.label),
        normalizeHeader(field.name),
        ...(synonyms[field.name] || []).map(normalizeHeader),
    ];

    /** Mejor atributo para un nombre de columna, o `null` si ninguno destaca lo suficiente. */
    const suggestFieldByName = (header: string): string | null => {
        const normalized = normalizeHeader(header);
        // Las columnas sin encabezado se nombran ColumnaN: ahi no hay nada que comparar.
        if (!normalized || /^columna\d+$/.test(normalized)) return null;

        const scored = fields
            .map((field) => ({
                name: field.name,
                score: Math.max(...candidatesFor(field).map((candidate) => similarity(normalized, candidate))),
            }))
            .sort((a, b) => b.score - a.score);

        const [best, second] = scored;
        if (!best || best.score < NAME_SCORE_THRESHOLD) return null;
        if (second && best.score - second.score < NAME_SCORE_MARGIN) return null;

        return best.name;
    };

    const suggestFieldByContent = (values: string[]): string | null => {
        if (!detectByContent) return null;

        const sample = values
            .map((value) => `${value ?? ""}`.trim())
            .filter(Boolean)
            .slice(0, CONTENT_SAMPLE_SIZE);
        if (sample.length < 2) return null;

        return detectByContent(sample);
    };

    const suggestColumnSuggestions = (columnNames: string[], dataRows: string[][]): ColumnSuggestion[] => {
        const taken = new Set<string>();

        const scored = columnNames.map((name, columnIndex) => {
            const values = dataRows.map((row) => row?.[columnIndex] ?? "");
            const byName = suggestFieldByName(name);
            const byContent = suggestFieldByContent(values);

            // Coincidir por nombre y por contenido a la vez es la senal mas fuerte.
            const field = byName || byContent;
            const confidence: MappingConfidence = byName && byContent && byName === byContent ? "high" : "low";

            return { columnIndex, field, confidence, agreed: confidence === "high" };
        });

        // Un atributo solo puede recibir una columna: gana la de mayor confianza.
        return [...scored]
            .sort((a, b) => Number(b.agreed) - Number(a.agreed) || a.columnIndex - b.columnIndex)
            .map((suggestion) => {
                if (!suggestion.field || taken.has(suggestion.field)) {
                    return { columnIndex: suggestion.columnIndex, field: null, confidence: "low" as MappingConfidence };
                }
                taken.add(suggestion.field);
                return {
                    columnIndex: suggestion.columnIndex,
                    field: suggestion.field,
                    confidence: suggestion.confidence,
                };
            })
            .sort((a, b) => a.columnIndex - b.columnIndex);
    };

    const suggestColumnMapping = (columnNames: string[], dataRows: string[][]): ColumnMapping =>
        suggestColumnSuggestions(columnNames, dataRows).reduce<ColumnMapping>(
            (mapping, suggestion) => ({ ...mapping, [suggestion.columnIndex]: suggestion.field }),
            {}
        );

    return { suggestFieldByName, suggestFieldByContent, suggestColumnSuggestions, suggestColumnMapping };
};
